import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getFailedConversation, getFailedConversations } from "@/services/analytics/getAnalytics";
import type {
  TAnalyticsErrorType,
  TFailedConversationDetail,
  TFailedConversationMeta,
} from "@/types/TAnalytics";

type ErrorAnalysisProps = {
  brokerErrors: TAnalyticsErrorType[];
  claimErrors: TAnalyticsErrorType[];
  days?: number;
};

const ERROR_TYPE_LABELS: Record<string, string> = {
  invalid_vsnr: "Ungültige VSNR",
  invalid_plz: "Ungültige PLZ",
  invalid_schadennummer: "Ungültige Schadennummer",
  api_error: "API-Fehler",
  api_not_configured: "API nicht konfiguriert",
  not_found: "Nicht gefunden",
  auth_failed: "Authentifizierung fehlgeschlagen",
  internal_error: "Interner Fehler",
};

function ErrorBar({ type, count, total, color }: { type: string; count: number; total: number; color: string }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const label = ERROR_TYPE_LABELS[type] || type;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-gray-900 dark:text-white font-medium">
          {count} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function formatTimestamp(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function sourceLabel(source: string, t: (key: string, fallback: string) => string) {
  if (source === "broker") return t("analytics.errors.broker", "Berater-Abfragen");
  if (source === "claim_status") return t("analytics.errors.claim", "Schadenstatus-Abfragen");
  return source;
}

function formatFormFields(fields?: TFailedConversationMeta["form_fields"] | null) {
  if (!fields) return "—";
  const parts: string[] = [];
  if (fields.vsnr) parts.push(`VSNR: ${fields.vsnr}`);
  if (fields.schadennummer) parts.push(`Schadennr.: ${fields.schadennummer}`);
  if (fields.plz) parts.push(`PLZ: ${fields.plz}`);
  return parts.length ? parts.join(" · ") : "—";
}

export function ErrorAnalysis({ brokerErrors, claimErrors, days = 30 }: ErrorAnalysisProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<TFailedConversationMeta[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TFailedConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const brokerFailed = brokerErrors.reduce((sum, e) => sum + e.count, 0);
  const claimFailed = claimErrors.reduce((sum, e) => sum + e.count, 0);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await getFailedConversations(days);
      setItems(data);
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Failed to load");
      setItems([]);
    } finally {
      setListLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    getFailedConversation(selectedId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          {t("analytics.errors.title", "Fehleranalyse")}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
              {t("analytics.errors.broker", "Berater-Abfragen")} ({brokerFailed}{" "}
              {t("analytics.errors.failed", "fehlgeschlagen")})
            </h4>
            {brokerErrors.length > 0 ? (
              <div className="space-y-3">
                {brokerErrors.map((error) => (
                  <ErrorBar
                    key={error.type}
                    type={error.type}
                    count={error.count}
                    total={brokerFailed}
                    color="bg-blue-500"
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("analytics.errors.noErrors", "Keine Fehler")}
              </p>
            )}
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
              {t("analytics.errors.claim", "Schadenstatus-Abfragen")} ({claimFailed}{" "}
              {t("analytics.errors.failed", "fehlgeschlagen")})
            </h4>
            {claimErrors.length > 0 ? (
              <div className="space-y-3">
                {claimErrors.map((error) => (
                  <ErrorBar
                    key={error.type}
                    type={error.type}
                    count={error.count}
                    total={claimFailed}
                    color="bg-green-500"
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("analytics.errors.noErrors", "Keine Fehler")}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("analytics.errors.conversationsTitle", "Fehlgeschlagene Verläufe")}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t(
              "analytics.errors.conversationsHint",
              "Chatverläufe zu fehlgeschlagenen Berater- und Schadenstatus-Abfragen (Retention 30 Tage)."
            )}
          </p>
        </div>

        {listLoading ? (
          <div className="flex items-center gap-2 text-gray-500 py-6">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{t("analytics.loading", "Lade Analytics...")}</span>
          </div>
        ) : listError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{listError}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("analytics.errors.noConversations", "Keine gespeicherten Fehlerverläufe")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3 font-medium">{t("analytics.errors.colTime", "Zeit")}</th>
                  <th className="py-2 pr-3 font-medium">{t("analytics.errors.colSource", "Quelle")}</th>
                  <th className="py-2 pr-3 font-medium">{t("analytics.errors.colError", "Fehlertyp")}</th>
                  <th className="py-2 pr-3 font-medium">{t("analytics.errors.colForm", "Eingaben")}</th>
                  <th className="py-2 font-medium">{t("analytics.errors.colMessages", "Nachrichten")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                    onClick={() => setSelectedId(item.id)}
                  >
                    <td className="py-2.5 pr-3 text-gray-900 dark:text-white whitespace-nowrap">
                      {formatTimestamp(item.timestamp)}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700 dark:text-gray-300">
                      {sourceLabel(item.source, t)}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700 dark:text-gray-300">
                      {ERROR_TYPE_LABELS[item.error_type || ""] || item.error_type || "—"}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {formatFormFields(item.form_fields)}
                    </td>
                    <td className="py-2.5 text-gray-700 dark:text-gray-300">
                      {item.message_count ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selectedId} onOpenChange={(open) => !open && setSelectedId(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {t("analytics.errors.conversationDetail", "Chatverlauf")}
            </DialogTitle>
            <DialogDescription>
              {detail
                ? `${sourceLabel(detail.source, t)} · ${ERROR_TYPE_LABELS[detail.error_type || ""] || detail.error_type || "—"} · ${formatTimestamp(detail.timestamp)}`
                : t("analytics.loading", "Lade Analytics...")}
            </DialogDescription>
          </DialogHeader>

          {detail?.form_fields && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
              <div className="font-medium text-gray-900 dark:text-white mb-1">
                {t("analytics.errors.formFieldsTitle", "Dialog-Eingaben")}
              </div>
              <div>{formatFormFields(detail.form_fields)}</div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-[200px]">
            {detailLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : !detail || !detail.messages?.length ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-6">
                {t("analytics.errors.noMessages", "Kein Chatverlauf gespeichert")}
              </p>
            ) : (
              detail.messages.map((msg, index) => {
                const isUser = msg.role === "user";
                return (
                  <div
                    key={`${msg.timestamp ?? "t"}-${index}`}
                    className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                        isUser
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      <div className="text-[11px] opacity-70 mb-1">
                        {isUser
                          ? t("analytics.errors.roleUser", "Nutzer")
                          : t("analytics.errors.roleAssistant", "Assistent")}
                        {msg.timestamp ? ` · ${formatTimestamp(msg.timestamp)}` : ""}
                      </div>
                      {msg.content}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
